import { useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { useCreateLabor, useLabor, useWorkers } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { SiteWorker, Trade, Uuid } from '@/lib/types';
import { WorkersTab } from '@/pages/WorkersPage';

/** `ck_pmis_labor_records_trade` 와 같은 목록이다. 여기서 새 값을 만들면 서버가 거부한다. */
const TRADES: Trade[] = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'];

type Tab = 'labor' | 'workers';

/**
 * 출역 · 투입 인력 — 한 화면의 두 탭 (사용자 결정 2026-09-10 · DOC-84 §7 ②).
 *
 * <p>두 표는 다르다. 출역(`pmis_labor_records`)은 <b>그날 몇 명</b>이고, 투입 인력(`site_workers`)은
 * <b>누가 들어와 있나</b>다. 표를 잇지는 않았다(DOC-82 §6-3-7) — 대신 출역을 적을 때 투입 인력에서
 * 인원수를 <b>바로 가져온다</b>. 매일 같은 사람이 나오는 현장에서 인원을 다시 세지 않게 하는 것이
 * 두 화면을 한 화면에 둔 이유다.
 *
 * <p>탭은 주소(`?tab=workers`)가 든다 — 위젯의 「전체보기」와 옛 `/workers` 주소가 이 탭을 바로 연다.
 * 협력사는 <b>자기 업체 줄만</b> 쓴다(서버가 `vendor_id` 로 자른다). 감리는 현장 전체를 조회만 한다.
 */
export function LaborPage() {
  const { siteId, siteName, readOnly, partyType } = useSite();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'workers' ? 'workers' : 'labor';
  const [open, setOpen] = useState(false);
  const canWrite = !readOnly && partyType === 'VENDOR';

  function choose(next: Tab) {
    const search = new URLSearchParams(params);
    if (next === 'labor') search.delete('tab');
    else search.set('tab', next);
    setParams(search, { replace: true });
    setOpen(false);
  }

  return (
    <>
      <PageHeader
        title={partyType === 'SUPERVISOR' ? '출역·투입 인력 조회' : '출역·투입 인력'}
        subtitle={siteName}
        action={
          canWrite && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              등록
            </button>
          )
        }
      />

      <div className="mb-3 flex gap-1 rounded-md p-1" style={{ background: 'var(--bg-hover)' }} role="tablist">
        <TabButton active={tab === 'labor'} onClick={() => choose('labor')}>출역</TabButton>
        <TabButton active={tab === 'workers'} onClick={() => choose('workers')}>투입 인력</TabButton>
      </div>

      {tab === 'labor' ? (
        <LaborTab siteId={siteId} canWrite={canWrite} open={open} onClose={() => setOpen(false)} />
      ) : (
        <WorkersTab siteId={siteId} canWrite={canWrite} open={open} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className="flex-1 rounded px-3 py-1.5 text-ui-sm"
      style={active
        ? { background: 'var(--bg-card)', color: 'var(--accent)', fontWeight: 600 }
        : { color: 'var(--text-secondary)' }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * 출역 탭.
 *
 * <p>「투입 인력에서 가져오기」 — 지금 투입 중인 사람 중 고른 공종의 수를 인원 칸에 넣는다.
 * 그 공종에 아무도 없으면 투입 중인 전원이다. 넣은 뒤에도 손으로 고칠 수 있다 — 그날 안 나온
 * 사람은 사람이 안다.
 */
function LaborTab({ siteId, canWrite, open, onClose }: { siteId: Uuid; canWrite: boolean; open: boolean; onClose: () => void }) {
  const labor = useLabor(siteId);
  const workers = useWorkers(siteId, { enabled: canWrite });
  const create = useCreateLabor(siteId);

  const [logDate, setLogDate] = useState(today);
  const [trade, setTrade] = useState<Trade>('건축');
  const [headcount, setHeadcount] = useState('');
  const [workArea, setWorkArea] = useState('');

  const rows = labor.data ?? [];
  const active = (workers.data ?? []).filter((row: SiteWorker) => row.leftOn === null);
  const sameTrade = active.filter((row) => row.trade === trade);

  function bringFromWorkers() {
    const count = sameTrade.length > 0 ? sameTrade.length : active.length;
    setHeadcount(String(count));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      logDate,
      trade,
      headcount: Number(headcount),
      workArea: workArea.trim() === '' ? null : workArea.trim(),
    });
    onClose();
    setHeadcount('');
    setWorkArea('');
  }

  return (
    <>
      {open && canWrite && (
        <form onSubmit={onSubmit} className="card mb-3">
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>날짜</span>
            <input type="date" className="input-field" value={logDate} onChange={(e) => setLogDate(e.target.value)} required />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공종</span>
            <select className="input-field" value={trade} onChange={(e) => setTrade(e.target.value as Trade)}>
              {TRADES.map((it) => (
                <option key={it} value={it}>{it}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block">
            <span className="mb-1 flex items-center justify-between text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
              <span>인원</span>
              {active.length > 0 && (
                <button type="button" className="text-ui-2xs" style={{ color: 'var(--accent)' }} onClick={bringFromWorkers}>
                  투입 인력에서 가져오기 ({sameTrade.length > 0 ? sameTrade.length : active.length}명)
                </button>
              )}
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className="input-field"
              value={headcount}
              onChange={(e) => setHeadcount(e.target.value)}
              required
            />
          </label>
          <label className="mb-4 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>작업구역</span>
            <input type="text" className="input-field" value={workArea} onChange={(e) => setWorkArea(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '등록'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={labor.isPending}
        error={labor.isError}
        empty={rows.length === 0}
        emptyText="출역 기록이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-ui-md">{shortDate(row.logDate)}</div>
                <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {row.trade ?? '공종 미지정'}
                  {row.workArea !== null && ` · ${row.workArea}`}
                </div>
              </div>
              <div className="shrink-0 text-ui-lg font-semibold">{row.headcount}명</div>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
